import * as Phaser from 'phaser';
import { NavigationBar } from '../objects/NavigationBar';
import { transitionManager } from '../systems/TransitionManager';
import { soundManager } from '../systems/SoundManager';
import { DESIGN } from '../config/design';
import { eventTracker } from '../analytics/EventTracker';

export class DebugPanelScene extends Phaser.Scene {
  private eventLog: string[] = [];
  private logText!: Phaser.GameObjects.Text;
  private stateIndex = 1;
  private readonly states = ['IDLE', 'MOVING', 'AIMING', 'DROP', 'GRABBING', 'LIFTING', 'RESULT'];
  private updateTimer = 0;

  constructor() {
    super({ key: 'DebugPanelScene' });
  }

  create(): void {
    this.addBackground();
    this.addHeader();
    this.addNavigation();
    this.addStatePanel();
    this.addEventConsole();
    this.addMetricsPanel();
    this.addEnvironmentPanel();
    this.addControls();

    this.log('ENGINE_LOADED');
    this.log('ASSET_FETCH: toy_collection_01');

    eventTracker.track('debug_panel_open', {});
  }

  update(_time: number, delta: number): void {
    this.updateTimer += delta;
    if (this.updateTimer > 1000) {
      this.updateTimer = 0;
      this.simulateLiveValues();
    }
  }

  private addBackground(): void {
    const graphics = this.add.graphics();
    for (let y = 0; y < 720; y += 4) {
      const t = y / 720;
      const r = Math.floor(248 + (232 - 248) * t);
      const g = Math.floor(249 + (240 - 249) * t);
      const b = Math.floor(255 + (244 - 255) * t);
      graphics.fillStyle((r << 16) | (g << 8) | b, 1);
      graphics.fillRect(0, y, 1280, 4);
    }
  }

  private addHeader(): void {
    this.add.text(40, 90, 'CRANE_DEBUG_V1.0', {
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: '24px',
      color: DESIGN.colors.primary,
      fontStyle: 'bold',
    }).setOrigin(0, 0.5);

    const badge = this.add.container(380, 90);
    badge.add(this.add.circle(0, 0, 5, parseInt(DESIGN.colors.secondary.replace('#', '0x'), 16)));
    this.tweens.add({
      targets: badge.list[0],
      scale: 1.2,
      alpha: 0.5,
      duration: 1000,
      yoyo: true,
      repeat: -1,
    });
    badge.add(this.add.text(15, 0, 'CORE_LIVE', {
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: '12px',
      color: DESIGN.colors.secondary,
      fontStyle: 'bold',
    }).setOrigin(0, 0.5));
  }

  private addNavigation(): void {
    new NavigationBar(this, 0, 35, 'debug', (key) => this.handleNavigation(key));
  }

  private addStatePanel(): void {
    const container = this.add.container(40, 360);
    const w = 240;
    const h = 340;

    const shadow = this.add.rectangle(6, 6, w, h, 0x000000, 0.08);
    container.add(shadow);

    const bg = this.add.rectangle(0, 0, w, h, 0xffffff, 0.85);
    bg.setStrokeStyle(1, parseInt(DESIGN.colors.outlineVariant.replace('#', '0x'), 16));
    container.add(bg);

    container.add(this.add.text(-w / 2 + 15, -h / 2 + 25, 'Machine_State', {
      fontFamily: DESIGN.fonts.headline,
      fontSize: '14px',
      color: DESIGN.colors.secondary,
      fontStyle: 'bold',
    }).setOrigin(0, 0.5));

    container.add(this.add.text(w / 2 - 15, -h / 2 + 25, '🧠', { fontSize: '16px' }).setOrigin(1, 0.5));

    this.states.forEach((state, index) => {
      const y = -h / 2 + 70 + index * 35;
      const isActive = index === this.stateIndex;
      const rowBg = this.add.rectangle(0, y, w - 20, 30, isActive ? parseInt(DESIGN.colors.secondaryContainer.replace('#', '0x'), 16) : 0xffffff, isActive ? 1 : 0);
      container.add(rowBg);

      const dotColor = isActive ? parseInt(DESIGN.colors.secondary.replace('#', '0x'), 16) : parseInt(DESIGN.colors.outline.replace('#', '0x'), 16);
      const dot = this.add.circle(-w / 2 + 25, y, 4, dotColor);
      container.add(dot);

      if (isActive) {
        this.tweens.add({
          targets: dot,
          scale: 1.5,
          alpha: 0.5,
          duration: 800,
          yoyo: true,
          repeat: -1,
        });
      }

      container.add(this.add.text(-w / 2 + 40, y, state, {
        fontFamily: DESIGN.fonts.body,
        fontSize: '13px',
        color: isActive ? DESIGN.colors.onSurface : DESIGN.colors.outline,
        fontStyle: isActive ? 'bold' : 'normal',
      }).setOrigin(0, 0.5));
    });
  }

  private addEventConsole(): void {
    const container = this.add.container(300, 360);
    const w = 280;
    const h = 340;

    const shadow = this.add.rectangle(6, 6, w, h, 0x000000, 0.08);
    container.add(shadow);

    const bg = this.add.rectangle(0, 0, w, h, 0xffffff, 0.85);
    bg.setStrokeStyle(1, parseInt(DESIGN.colors.outlineVariant.replace('#', '0x'), 16));
    container.add(bg);

    container.add(this.add.text(-w / 2 + 15, -h / 2 + 25, 'Event_Console', {
      fontFamily: DESIGN.fonts.headline,
      fontSize: '14px',
      color: DESIGN.colors.primary,
      fontStyle: 'bold',
    }).setOrigin(0, 0.5));

    this.logText = this.add.text(-w / 2 + 15, -h / 2 + 55, '', {
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: '10px',
      color: DESIGN.colors.onSurfaceVariant,
      lineSpacing: 4,
    }).setOrigin(0, 0);
    container.add(this.logText);
  }

  private addMetricsPanel(): void {
    const container = this.add.container(600, 320);
    const w = 360;
    const h = 240;

    const shadow = this.add.rectangle(6, 6, w, h, 0x000000, 0.08);
    container.add(shadow);

    const bg = this.add.rectangle(0, 0, w, h, 0xffffff, 0.85);
    bg.setStrokeStyle(1, parseInt(DESIGN.colors.outlineVariant.replace('#', '0x'), 16));
    container.add(bg);

    container.add(this.add.text(-w / 2 + 15, -h / 2 + 25, 'Physics_Data_Grid', {
      fontFamily: DESIGN.fonts.headline,
      fontSize: '14px',
      color: DESIGN.colors.tertiary,
      fontStyle: 'bold',
    }).setOrigin(0, 0.5));

    const gridData = [
      { label: 'Success_Flag', value: 'TRUE', color: DESIGN.colors.secondary },
      { label: 'Near_Miss', value: 'FALSE', color: DESIGN.colors.fail },
      { label: 'Gravity_Y', value: '9.81 m/s²', color: DESIGN.colors.onSurface },
      { label: 'Claw_Tension', value: '42.8%', color: DESIGN.colors.onSurface },
      { label: 'Collision_Mask', value: '0x1102A', color: DESIGN.colors.tertiary },
      { label: 'Draw_Calls', value: '12', color: DESIGN.colors.onSurface },
    ];

    gridData.forEach((item, index) => {
      const col = index % 2;
      const row = Math.floor(index / 2);
      const x = -w / 2 + 30 + col * (w / 2);
      const y = -h / 2 + 70 + row * 55;

      container.add(this.add.text(x, y, item.label, {
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: '10px',
        color: DESIGN.colors.onSurfaceVariant,
        fontStyle: 'bold',
      }).setOrigin(0, 0.5));

      container.add(this.add.text(x, y + 20, item.value, {
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: '16px',
        color: item.color,
        fontStyle: 'bold',
      }).setOrigin(0, 0.5));
    });

    // Accuracy card
    const accContainer = this.add.container(600, 530);
    const accW = 170;
    const accH = 120;

    accContainer.add(this.add.rectangle(6, 6, accW, accH, 0x000000, 0.08));
    accContainer.add(this.add.rectangle(0, 0, accW, accH, 0xffffff, 0.85).setStrokeStyle(1, parseInt(DESIGN.colors.outlineVariant.replace('#', '0x'), 16)));
    accContainer.add(this.add.text(0, -35, 'Accuracy', {
      fontFamily: DESIGN.fonts.body,
      fontSize: '10px',
      color: DESIGN.colors.onSurfaceVariant,
      fontStyle: 'bold',
    }).setOrigin(0.5));
    accContainer.add(this.add.text(0, -10, '0.85', {
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: '36px',
      color: DESIGN.colors.secondary,
      fontStyle: 'bold',
    }).setOrigin(0.5));
    accContainer.add(this.add.text(60, 0, 'RAD', {
      fontFamily: DESIGN.fonts.body,
      fontSize: '10px',
      color: DESIGN.colors.secondary,
    }).setOrigin(0, 0.5));

    const barBg = this.add.rectangle(0, 25, accW - 20, 8, parseInt(DESIGN.colors.surfaceContainer.replace('#', '0x'), 16));
    accContainer.add(barBg);
    const barFill = this.add.rectangle(-(accW - 20) / 2 + (accW - 20) * 0.85 / 2, 25, (accW - 20) * 0.85, 8, parseInt(DESIGN.colors.secondaryContainer.replace('#', '0x'), 16));
    accContainer.add(barFill);

    // Session time card
    const timeContainer = this.add.container(790, 530);
    const timeW = 170;
    const timeH = 120;

    timeContainer.add(this.add.rectangle(6, 6, timeW, timeH, 0x000000, 0.08));
    timeContainer.add(this.add.rectangle(0, 0, timeW, timeH, 0xffffff, 0.85).setStrokeStyle(1, parseInt(DESIGN.colors.outlineVariant.replace('#', '0x'), 16)));
    timeContainer.add(this.add.text(0, -35, 'Session Time', {
      fontFamily: DESIGN.fonts.body,
      fontSize: '10px',
      color: DESIGN.colors.onSurfaceVariant,
      fontStyle: 'bold',
    }).setOrigin(0.5));
    timeContainer.add(this.add.text(0, -5, '12:44', {
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: '36px',
      color: DESIGN.colors.onSurface,
      fontStyle: 'bold',
    }).setOrigin(0.5));
  }

  private addEnvironmentPanel(): void {
    const container = this.add.container(990, 360);
    const w = 240;
    const h = 340;

    const shadow = this.add.rectangle(6, 6, w, h, 0x000000, 0.08);
    container.add(shadow);

    const bg = this.add.rectangle(0, 0, w, h, 0xffffff, 0.85);
    bg.setStrokeStyle(1, parseInt(DESIGN.colors.outlineVariant.replace('#', '0x'), 16));
    container.add(bg);

    container.add(this.add.text(-w / 2 + 15, -h / 2 + 25, 'Environment', {
      fontFamily: DESIGN.fonts.headline,
      fontSize: '14px',
      color: DESIGN.colors.onSurfaceVariant,
      fontStyle: 'bold',
    }).setOrigin(0, 0.5));

    const envData = [
      { icon: '🖥️', label: 'DESKTOP_WIN32', color: DESIGN.colors.primary },
      { icon: '🎮', label: 'PHASER_V3.90', color: DESIGN.colors.secondary },
      { icon: '⚡', label: 'FPS: 60.01', color: DESIGN.colors.tertiary },
    ];

    envData.forEach((item, index) => {
      const y = -h / 2 + 75 + index * 55;
      const rowBg = this.add.rectangle(0, y, w - 20, 42, parseInt(DESIGN.colors.surfaceContainer.replace('#', '0x'), 16));
      rowBg.setStrokeStyle(1, parseInt(DESIGN.colors.outlineVariant.replace('#', '0x'), 16), 0.3);
      container.add(rowBg);

      container.add(this.add.text(-w / 2 + 25, y, item.icon, { fontSize: '18px' }).setOrigin(0, 0.5));
      container.add(this.add.text(-w / 2 + 55, y, item.label, {
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: '11px',
        color: item.color,
        fontStyle: 'bold',
      }).setOrigin(0, 0.5));
    });

    const protoBadge = this.add.rectangle(0, h / 2 - 40, w - 20, 50, parseInt(DESIGN.colors.surfaceContainerHigh.replace('#', '0x'), 16), 0.1);
    protoBadge.setStrokeStyle(2, parseInt(DESIGN.colors.outlineVariant.replace('#', '0x'), 16), 1);
    container.add(protoBadge);
    container.add(this.add.text(0, h / 2 - 40, 'PROTOTYPE_BUILD_429-X\nNOT_FOR_PUBLIC_RELEASE', {
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: '9px',
      color: DESIGN.colors.onSurfaceVariant,
      align: 'center',
    }).setOrigin(0.5));
  }

  private addControls(): void {
    const container = this.add.container(990, 70);
    const w = 240;
    const h = 80;

    const exportBtn = this.add.rectangle(0, 0, w, h, parseInt(DESIGN.colors.primary.replace('#', '0x'), 16));
    exportBtn.setInteractive({ useHandCursor: true });
    container.add(exportBtn);
    container.add(this.add.text(0, 0, '📤 EXPORT_LOGS', {
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: '12px',
      color: '#ffffff',
      fontStyle: 'bold',
    }).setOrigin(0.5));

    exportBtn.on('pointerdown', () => {
      soundManager.play('click');
      this.exportLogs();
    });
  }

  private log(message: string): void {
    const time = new Date().toLocaleTimeString('en-US', { hour12: false });
    this.eventLog.unshift(`[${time}] ${message}`);
    if (this.eventLog.length > 12) this.eventLog.pop();
    this.logText.setText(this.eventLog.join('\n'));
  }

  private simulateLiveValues(): void {
    this.stateIndex = (this.stateIndex + 1) % this.states.length;

    this.log(`STATE_CHANGE: ${this.states[this.stateIndex]}`);
    this.log(`CLAW_POSITION_X: ${(180 + Math.random() * 200).toFixed(2)}`);
    this.log(`PHYSICS_UPDATE: ${58 + Math.floor(Math.random() * 4)}fps`);

    this.scene.restart();
  }

  private exportLogs(): void {
    if (typeof window === 'undefined') return;
    const blob = new Blob([this.eventLog.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `debug-logs-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    eventTracker.track('debug_export_logs', { count: this.eventLog.length });
  }

  private handleNavigation(key: string): void {
    soundManager.play('click');
    switch (key) {
      case 'play':
        transitionManager.go(this, 'GameScene');
        break;
      case 'collection':
        transitionManager.go(this, 'CollectionScene');
        break;
      case 'settings':
        transitionManager.go(this, 'SettingsScene');
        break;
      case 'analytics':
        transitionManager.go(this, 'AnalyticsDashboardScene');
        break;
    }
  }
}
